import React, { useState, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress
} from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useNavigate } from 'react-router-dom';
import { adminInstance } from '../../../utils/axios';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const CreateArticle = () => {
    const navigate = useNavigate();
    const editorRef = useRef(null);
    const [article, setArticle] = useState({
        title: '',
        summary: '',
        category: '',
        content: '',
        status: 'draft'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [generatingContent, setGeneratingContent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Get content from editor
            if (editorRef.current) {
                article.content = editorRef.current.getContent();
            }

            // Validate fields
            if (!article.title || !article.category || !article.content) {
                throw new Error('Please fill in all required fields');
            }

            await adminInstance.post('/api/admin/articles', article);
            setSuccess(true);
            setLoading(false);

            // Redirect after success
            setTimeout(() => {
                navigate('/admin/articles');
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Error creating article');
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setArticle(prev => ({ ...prev, [name]: value }));
    };

    const handleEditorChange = (content) => {
        setArticle(prev => ({ ...prev, content }));
    };

    const handleAiGenerate = async () => {
        if (!aiPrompt) {
            return;
        }

        setGeneratingContent(true);
        try {
            const response = await adminInstance.post('/api/admin/articles/generate', {
                prompt: aiPrompt
            });

            if (response.data) {
                // Update editor content with generated text
                if (editorRef.current) {
                    editorRef.current.setContent(response.data.content);
                }
                
                // Update other fields if provided
                if (response.data.title) {
                    setArticle(prev => ({
                        ...prev,
                        title: response.data.title || prev.title,
                        summary: response.data.summary || prev.summary,
                        content: response.data.content
                    }));
                }
            }
            
            setAiDialogOpen(false);
        } catch (err) {
            setError('Failed to generate article: ' + (err.response?.data?.message || err.message));
        } finally {
            setGeneratingContent(false);
        }
    };

    return (
        <Container>
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
                    Create New Article
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                        Article created successfully!
                    </Alert>
                )}

                <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Article Information
                                </Typography>
                                <TextField
                                    required
                                    fullWidth
                                    label="Title"
                                    name="title"
                                    value={article.title}
                                    onChange={handleChange}
                                    disabled={loading}
                                    sx={{ mb: 3 }}
                                />

                                <Box sx={{ mb: 3 }}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            name="category"
                                            value={article.category}
                                            label="Category"
                                            onChange={handleChange}
                                            disabled={loading}
                                        >
                                            <MenuItem value="medical">Medical</MenuItem>
                                            <MenuItem value="education">Education</MenuItem>
                                            <MenuItem value="exam-prep">Exam Preparation</MenuItem>
                                            <MenuItem value="career">Career</MenuItem>
                                            <MenuItem value="research">Research</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <FormControl fullWidth required>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={article.status}
                                            label="Status"
                                            onChange={handleChange}
                                            disabled={loading}
                                        >
                                            <MenuItem value="draft">Draft</MenuItem>
                                            <MenuItem value="published">Published</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="Summary"
                                        name="summary"
                                        multiline
                                        rows={3}
                                        value={article.summary}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Article Content
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<AutoFixHighIcon />}
                                    onClick={() => setAiDialogOpen(true)}
                                    sx={{ mb: 3 }}
                                    disabled={loading}
                                >
                                    Generate with AI
                                </Button>
                                
                                <Editor
                                    onInit={(evt, editor) => editorRef.current = editor}
                                    apiKey="nf6iwxns5fncla6vcv4zgx3rdsbh3k8k7xcsnxfwiuf9pk2o"
                                    init={{
                                        height: 500,
                                        menubar: true,
                                        plugins: [
                                            // Core editing features
                                            'anchor', 'autolink', 'charmap', 'codesample', 'emoticons', 'image', 'link', 'lists', 'media', 'searchreplace', 'table', 'visualblocks', 'wordcount',
                                            // Premium features
                                            'checklist', 'mediaembed', 'casechange', 'formatpainter', 'pageembed', 'a11ychecker', 'tinymcespellchecker', 'permanentpen', 'powerpaste', 'advtable', 'advcode', 'editimage', 'advtemplate', 'ai', 'mentions', 'tinycomments', 'tableofcontents', 'footnotes', 'mergetags', 'autocorrect', 'typography', 'inlinecss', 'markdown','importword', 'exportword', 'exportpdf'
                                        ],
                                        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table mergetags | addcomment showcomments | spellcheckdialog a11ycheck typography | align lineheight | checklist numlist bullist indent outdent | emoticons charmap | removeformat',
                                        tinycomments_mode: 'embedded',
                                        tinycomments_author: 'Admin',
                                        mergetags_list: [
                                            { value: 'First.Name', title: 'First Name' },
                                            { value: 'Email', title: 'Email' },
                                        ],
                                        ai_request: (request, respondWith) => respondWith.string(() => Promise.reject('See docs to implement AI Assistant')),
                                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                                    }}
                                    initialValue="<p>Write your article content here...</p>"
                                    onEditorChange={handleEditorChange}
                                    disabled={loading}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/admin/articles')}
                                    disabled={loading}
                                    size="large"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                    size="large"
                                    sx={{ ml: 2 }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Create Article'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* AI Generation Dialog */}
            <Dialog open={aiDialogOpen} onClose={() => !generatingContent && setAiDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle variant="h5">Generate Article with AI</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ pt: 1 }}>
                        <Typography variant="body1" gutterBottom>
                            Describe the article you want to generate. Be as specific as possible about the topic, 
                            target audience, and any key points you want to include.
                        </Typography>
                        <TextField
                            autoFocus
                            fullWidth
                            multiline
                            rows={6}
                            label="Prompt"
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            disabled={generatingContent}
                            placeholder="Example: Create an informative article about diabetes management for medical students. Include sections on types of diabetes, symptoms, diagnosis, and treatment options."
                            sx={{ mt: 2 }}
                            variant="outlined"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setAiDialogOpen(false)} 
                        disabled={generatingContent}
                        size="large"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAiGenerate} 
                        variant="contained" 
                        disabled={!aiPrompt || generatingContent}
                        startIcon={generatingContent ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                        size="large"
                    >
                        {generatingContent ? 'Generating...' : 'Generate Article'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CreateArticle; 